import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// 定義關鍵幀動畫
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const waveAnimation = keyframes`
  0% { transform: translateX(0); }
  50% { transform: translateX(-10px); }
  100% { transform: translateX(0); }
`;

// Hero section styled components
const HeroSection = styled.section`
  position: relative;
  padding: 140px 0 100px;
  background: linear-gradient(135deg, #4338CA 0%, #7C3AED 100%);
  color: white;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 120px 0 80px;
  }
`;

const HeroContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 600px;
  
  @media (max-width: 992px) {
    max-width: 100%;
    margin-bottom: 3rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--primary);
  padding: 1rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: all var(--transition-normal);
  
  &:hover {
    background-color: var(--bg-light);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: white;
  padding: 1rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.5);
  transition: all var(--transition-normal);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    color: white;
  }
`;

const HeroImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeroIllustration = styled.div`
  animation: ${floatAnimation} 6s ease-in-out infinite;
  max-width: 100%;
`;

// Styled SVG Group Components
const PulseGroup = styled.g`
  animation: ${pulseAnimation} 3s ease-in-out infinite;
`;

const FloatingGroup1 = styled.g`
  animation: ${floatAnimation} 4s ease-in-out infinite;
`;

const FloatingGroup2 = styled.g`
  animation: ${floatAnimation} 5s ease-in-out infinite 1s;
`;

const WaveGroup = styled.g`
  animation: ${waveAnimation} 7s ease-in-out infinite;
`;

// Hero SVG Component
const HeroSVG = () => (
    <svg width="500" height="500" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Background Circles */}
        <circle cx="250" cy="250" r="200" fill="rgba(255, 255, 255, 0.05)" />
        <circle cx="250" cy="250" r="150" fill="rgba(255, 255, 255, 0.1)" />

        {/* Mobile Phone Frame */}
        <rect x="150" y="100" width="200" height="350" rx="20" fill="white" />
        <rect x="160" y="120" width="180" height="310" rx="10" fill="#F3F4F6" />

        {/* Phone Details */}
        <circle cx="250" cy="110" r="5" fill="#D1D5DB" />
        <rect x="230" y="430" width="40" height="10" rx="5" fill="#D1D5DB" />

        {/* App UI Elements */}
        {/* Header */}
        <rect x="160" y="120" width="180" height="50" fill="#4338CA" />
        <rect x="175" y="135" width="100" height="20" rx="5" fill="white" />
        <circle cx="315" cy="145" r="10" fill="white" />

        {/* Featured Product */}
        <rect x="175" y="180" width="150" height="100" rx="5" fill="white" />
        <rect x="185" y="190" width="130" height="60" rx="3" fill="#F59E0B" />
        <rect x="185" y="260" width="80" height="10" rx="3" fill="#4B5563" />
        <rect x="185" y="210" width="50" height="10" rx="2" fill="white" />

        {/* Product Grid */}
        <rect x="175" y="290" width="70" height="70" rx="5" fill="white" />
        <rect x="255" y="290" width="70" height="70" rx="5" fill="white" />
        <rect x="175" y="370" width="70" height="70" rx="5" fill="white" />
        <rect x="255" y="370" width="70" height="70" rx="5" fill="white" />

        {/* Shopping Cart Icon with Animation */}
        <PulseGroup>
            <circle cx="315" cy="410" r="20" fill="#F59E0B" />
            <path d="M305,410 L310,403 L320,403 L325,410" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <circle cx="310" cy="415" r="2" fill="white" />
            <circle cx="320" cy="415" r="2" fill="white" />
        </PulseGroup>

        {/* Floating Elements */}
        <FloatingGroup1>
            <circle cx="400" cy="150" r="25" fill="#F59E0B" opacity="0.8" />
            <path d="M390,150 L400,140 L410,150" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <path d="M400,140 L400,160" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </FloatingGroup1>

        <FloatingGroup2>
            <circle cx="100" cy="200" r="30" fill="#8B5CF6" opacity="0.8" />
            <rect x="90" y="190" width="20" height="20" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
        </FloatingGroup2>

        {/* Wave Elements */}
        <WaveGroup>
            <path d="M50,280 C70,260 90,300 110,280 C130,260 150,300 170,280"
                stroke="#F59E0B"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round" />

            <path d="M70,320 C90,300 110,340 130,320 C150,300 170,340 190,320"
                stroke="#8B5CF6"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round" />

            <path d="M40,360 C60,340 80,380 100,360 C120,340 140,380 160,360"
                stroke="#4338CA"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round" />
        </WaveGroup>

        {/* Abstract elements */}
        <circle cx="420" cy="350" r="15" fill="#4338CA" opacity="0.8" />
        <circle cx="450" cy="380" r="10" fill="#8B5CF6" opacity="0.6" />
        <circle cx="400" cy="400" r="20" fill="#F59E0B" opacity="0.7" />
    </svg>
);

const Hero = () => {
    return (
        <HeroSection>
            <HeroContainer>
                <HeroContent>
                    <HeroTitle>打造您的專屬行動商城，觸及更多客戶</HeroTitle>
                    <HeroSubtitle>
                        ShopWave提供完整的行動購物應用解決方案，幫助您的業務快速轉型，無論是蔬果、美妝、服飾還是電子產品。
                    </HeroSubtitle>
                    <ButtonGroup>
                        <PrimaryButton to="/contact">立即開始</PrimaryButton>
                        <SecondaryButton to="#case-studies">查看示範</SecondaryButton>
                    </ButtonGroup>
                </HeroContent>
                <HeroImageContainer>
                    <HeroIllustration>
                        <HeroSVG />
                    </HeroIllustration>
                </HeroImageContainer>
            </HeroContainer>
        </HeroSection>
    );
};

export default Hero;